import MiniSearch from "minisearch";
import type { SiteManifest, PublishedPage } from "./types";
import { fetchSearchIndex, fetchPage } from "./api";

export interface SearchDoc {
  id: string;
  pageUuid: string;
  pageSlug: string;
  pageTitle: string;
  pageEmoji?: string | null;
  pageDescription?: string | null;
  folderUuid?: string | null;
  folderName?: string | null;
  sectionTitle: string;
  anchor: string | null;
  headings: string;
  body: string;
}

export interface SearchIndexPayload {
  version: number;
  generated_at?: string;
  docs: SearchDoc[];
}

export interface SearchHit {
  id: string;
  pageUuid: string;
  pageSlug: string;
  pageTitle: string;
  pageEmoji?: string | null;
  folderName?: string | null;
  sectionTitle: string;
  anchor: string | null;
  snippet: string;
  score: number;
  isSection: boolean;
}

const SECTION_BODY_MAX = 1200;

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

interface Section {
  anchor: string | null;
  sectionTitle: string;
  body: string;
}

// Same algorithm as server-side buildSectionsFromHtml so local dev ≈ production.
export function buildSectionsFromHtml(html: string): Section[] {
  if (!html) return [];
  const sections: Section[] = [];
  const anchorCounts = new Map<string, number>();
  const re = /<h([23])[^>]*>([\s\S]*?)<\/h\1>/gi;
  let lastIdx = 0;
  let current: Section = { anchor: null, sectionTitle: "", body: "" };
  let match: RegExpExecArray | null;

  const flush = () => {
    const body = current.body.replace(/\s+/g, " ").trim();
    if (body || current.sectionTitle) {
      sections.push({
        anchor: current.anchor,
        sectionTitle: current.sectionTitle,
        body: body.slice(0, SECTION_BODY_MAX)
      });
    }
  };

  while ((match = re.exec(html))) {
    current.body += " " + stripHtml(html.slice(lastIdx, match.index));
    flush();
    const title = stripHtml(match[2]);
    let anchor = slugifyHeading(title);
    const count = anchorCounts.get(anchor) ?? 0;
    anchorCounts.set(anchor, count + 1);
    if (count > 0) anchor = `${anchor}-${count}`;
    current = { anchor, sectionTitle: title, body: "" };
    lastIdx = re.lastIndex;
  }

  current.body += " " + stripHtml(html.slice(lastIdx));
  flush();

  return sections;
}

export function buildSearchDocsFromManifest(
  manifest: SiteManifest,
  pages: PublishedPage[]
): SearchDoc[] {
  const folderByUuid = new Map(manifest.folders.map((f) => [f.uuid, f]));
  const pageMetaBySlug = new Map(manifest.pages.map((p) => [p.slug, p]));
  const docs: SearchDoc[] = [];

  for (const page of pages) {
    const meta = pageMetaBySlug.get(page.slug);
    if (!meta) continue;
    const folder = folderByUuid.get(meta.folder_uuid);

    const sections = buildSectionsFromHtml(page.htmlContent || "");
    const headingsJoined = sections
      .map((s) => s.sectionTitle)
      .filter(Boolean)
      .join(" · ");

    docs.push({
      id: page.uuid,
      pageUuid: page.uuid,
      pageSlug: page.slug,
      pageTitle: page.title,
      pageEmoji: page.emoji ?? null,
      pageDescription: page.description ?? null,
      folderUuid: meta.folder_uuid,
      folderName: folder?.name ?? null,
      sectionTitle: page.title,
      anchor: null,
      headings: headingsJoined,
      body: sections
        .map((s) => s.body)
        .join(" ")
        .slice(0, SECTION_BODY_MAX)
    });

    for (const section of sections) {
      if (!section.anchor) continue;
      docs.push({
        id: `${page.uuid}#${section.anchor}`,
        pageUuid: page.uuid,
        pageSlug: page.slug,
        pageTitle: page.title,
        pageEmoji: page.emoji ?? null,
        pageDescription: page.description ?? null,
        folderUuid: meta.folder_uuid,
        folderName: folder?.name ?? null,
        sectionTitle: section.sectionTitle,
        anchor: section.anchor,
        headings: section.sectionTitle,
        body: section.body
      });
    }
  }

  return docs;
}

// ─── MiniSearch index ────────────────────────────────────────────────────────

function createMiniSearch(docs: SearchDoc[]): MiniSearch<SearchDoc> {
  const mini = new MiniSearch<SearchDoc>({
    idField: "id",
    fields: [
      "pageTitle",
      "sectionTitle",
      "headings",
      "body",
      "pageDescription"
    ],
    storeFields: [
      "pageUuid",
      "pageSlug",
      "pageTitle",
      "pageEmoji",
      "folderName",
      "sectionTitle",
      "anchor",
      "body"
    ],
    searchOptions: {
      boost: {
        pageTitle: 3,
        sectionTitle: 2,
        headings: 1.5,
        pageDescription: 1.5
      },
      fuzzy: 0.2,
      prefix: true,
      combineWith: "AND"
    }
  });
  mini.addAll(docs);
  return mini;
}

let instancePromise: Promise<MiniSearch<SearchDoc>> | null = null;

export async function ensureSearchIndex(
  manifest: SiteManifest
): Promise<MiniSearch<SearchDoc>> {
  if (instancePromise) return instancePromise;

  instancePromise = (async () => {
    // Prefer server-emitted search-index.json (production + fixtured dev).
    try {
      const payload = await fetchSearchIndex();
      if (payload?.docs?.length) return createMiniSearch(payload.docs);
    } catch {
      // fall through to client-built fallback
    }

    // Fallback: build client-side by fetching every published page.
    const pagesMeta = manifest.pages ?? [];
    const pages = await Promise.all(
      pagesMeta.map((p) => fetchPage(p.slug).catch(() => null))
    );
    const filtered = pages.filter(
      (p): p is PublishedPage => !!p && typeof p.htmlContent === "string"
    );
    const docs = buildSearchDocsFromManifest(manifest, filtered);
    return createMiniSearch(docs);
  })();

  try {
    return await instancePromise;
  } catch (err) {
    // Reset on failure so a later retry can try again.
    instancePromise = null;
    throw err;
  }
}

export function resetSearchIndex() {
  instancePromise = null;
}

// ─── Snippet extraction ──────────────────────────────────────────────────────

function buildSnippet(body: string, terms: string[]): string {
  if (!body) return "";
  const lower = body.toLowerCase();
  let best = -1;
  let matchedLen = 0;
  for (const t of terms) {
    if (!t) continue;
    const idx = lower.indexOf(t.toLowerCase());
    if (idx >= 0 && (best === -1 || idx < best)) {
      best = idx;
      matchedLen = t.length;
    }
  }
  if (best === -1)
    return body.length > 140 ? body.slice(0, 140).trim() + "…" : body;

  const radius = 80;
  const start = Math.max(0, best - radius);
  const end = Math.min(body.length, best + matchedLen + radius);
  let snippet = body.slice(start, end).trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < body.length) snippet = snippet + "…";
  return snippet;
}

export function runSearch(
  mini: MiniSearch<SearchDoc>,
  query: string,
  limit = 8
): SearchHit[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const results = mini.search(trimmed);
  const terms: string[] = trimmed.split(/\s+/).filter(Boolean);

  const byPage = new Map<string, SearchHit>();

  for (const r of results) {
    const data = r as unknown as SearchDoc & { score: number };
    const snippet = buildSnippet(data.body || "", terms);
    const isSection = Boolean(data.anchor);

    const existing = byPage.get(data.pageUuid);
    const hit: SearchHit = {
      id: data.id,
      pageUuid: data.pageUuid,
      pageSlug: data.pageSlug,
      pageTitle: data.pageTitle,
      pageEmoji: data.pageEmoji ?? null,
      folderName: data.folderName ?? null,
      sectionTitle: data.sectionTitle,
      anchor: data.anchor,
      snippet,
      score: data.score,
      isSection
    };

    // Only one hit per page — prefer higher score; prefer section hit over page hit at equal score
    if (!existing) {
      byPage.set(data.pageUuid, hit);
    } else if (
      hit.score > existing.score ||
      (hit.score === existing.score && hit.isSection && !existing.isSection)
    ) {
      byPage.set(data.pageUuid, hit);
    }
  }

  return Array.from(byPage.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
